import { useSpring } from '@react-spring/three'
import { useFrame } from '@react-three/fiber'
import { Select } from '@react-three/postprocessing'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  CatmullRomCurve3,
  Group,
  MathUtils,
  QuadraticBezierCurve3,
  TubeGeometry,
  Vector3,
  type BufferGeometry,
} from 'three'

import { SphereHitbox } from '@components/helpers'
import { SplineModel, SwitchIndicatorModel } from '@components/models'
import type { Direction, TrackShape } from '@config'
import { isCellOccupied, SHAPES } from '@engine'
import { GamePhase, useGameStore, useSoundBoard } from '@stores'
import {
  buildRailsCurves,
  CORNER_OFFSETS,
  DIRECTION_OFFSETS,
  HALF_RAILS_GAUGE,
  LEFT_TUBE_SEGMENTS,
  METAL_MATERIAL,
  normalizeAngle,
  RADIAL_SEGMENTS,
  RAIL_HEIGHT,
  RAIL_RADIUS,
  RAIL_SCALE_Y,
  RIGHT_TUBE_SEGMENTS,
  sampleSplines,
  SPLINE_HEIGHT,
  SPLINES_COUNT,
} from '@utils'

const CENTER = new Vector3(0, 0, 0)

const ARROW_ROTATIONS: Record<Direction, number> = {
  N: MathUtils.degToRad(90),
  S: MathUtils.degToRad(-90),
  E: MathUtils.degToRad(0),
  W: MathUtils.degToRad(180),
}

function getSwitchLogic(shapes: TrackShape[], activeIndex: number) {
  const firstPorts = SHAPES[shapes[0]].ports
  const common = firstPorts.find(dir => shapes.every(shape => SHAPES[shape].ports.includes(dir)))!

  const activeShape = shapes[activeIndex]
  const activePorts = SHAPES[activeShape].ports
  const target = activePorts.find(dir => dir !== common)!

  return { common, target }
}

interface AnimatedSwitchTrackProps {
  x: number
  y: number
  shapes: TrackShape[]
  activeIndex: number
}

export function AnimatedSwitchTrack({ x, y, shapes, activeIndex }: AnimatedSwitchTrackProps) {
  const phase = useGameStore(s => s.phase)
  const handleToggleSwitch = useGameStore(s => s.handleToggleSwitch)
  const sounds = useSoundBoard(state => state.sounds)

  const { common, target } = useMemo(
    () => getSwitchLogic(shapes, activeIndex),
    [shapes, activeIndex],
  )
  const pivot = DIRECTION_OFFSETS[common]
  const targetOffset = DIRECTION_OFFSETS[target]
  const targetAngle = Math.atan2(targetOffset.z, targetOffset.x)
  const pivotAngle = Math.atan2(pivot.z, pivot.x)

  const angleRef = useRef(targetAngle)
  const leftGeoRef = useRef<BufferGeometry>(null)
  const rightGeoRef = useRef<BufferGeometry>(null)
  const splinesGroupsRef = useRef<(Group | null)[]>([])
  const prevAngleRef = useRef<number | null>(null)

  const isMountedRef = useRef(false)

  const [highlight, setHighlight] = useState(false)
  const [occupied, setOccupied] = useState<boolean>(false)
  const prevOccupied = useRef<boolean>(false)

  const [{ angle }, api] = useSpring(() => ({
    angle: targetAngle,
    config: { duration: 300 },
  }))

  useLayoutEffect(() => {
    const immediate = !isMountedRef.current
    isMountedRef.current = true
    let next = normalizeAngle(targetAngle, angleRef.current)
    // rotate away from pivot: if shortest path passes through pivot side, take the long way
    const from = angleRef.current
    const pivotNormalized = normalizeAngle(pivotAngle, from)
    const passesThroughPivot =
      Math.sign(next - from) === Math.sign(pivotNormalized - from) &&
      Math.abs(pivotNormalized - from) < Math.abs(next - from)
    if (passesThroughPivot) next += Math.PI * 2 * (next > from ? -1 : 1)
    angleRef.current = next
    api.start({ angle: next, immediate })
  })

  const pointPool = useMemo(
    () => ({
      point: new Vector3(),
      tangent: new Vector3(),
      normal: new Vector3(),
      free: new Vector3(),
      liveCurve: new QuadraticBezierCurve3(new Vector3(), new Vector3(), new Vector3()),
      leftCatmull: new CatmullRomCurve3(Array.from({ length: 21 }, () => new Vector3())),
      rightCatmull: new CatmullRomCurve3(Array.from({ length: 21 }, () => new Vector3())),
    }),
    [],
  )

  useFrame(() => {
    const { network } = useGameStore.getState()
    if (network) {
      const nextOccupied = isCellOccupied(network, x, y)
      if (nextOccupied !== prevOccupied.current) {
        prevOccupied.current = nextOccupied
        setOccupied(nextOccupied)
        if (nextOccupied) setHighlight(false)
      }
    }

    const a = angle.get()
    if (a === prevAngleRef.current) return
    prevAngleRef.current = a

    const { point, tangent, normal, free, liveCurve, leftCatmull, rightCatmull } = pointPool
    free.set(Math.cos(a) * 0.5, 0, Math.sin(a) * 0.5)

    liveCurve.v0.copy(pivot)
    liveCurve.v1.copy(CENTER)
    liveCurve.v2.copy(free)
    liveCurve.updateArcLengths()

    for (let i = 0; i <= 20; i++) {
      const t = i / 20
      liveCurve.getPointAt(t, point)
      liveCurve.getTangentAt(t, tangent).normalize()
      normal.set(-tangent.z, 0, tangent.x)
      leftCatmull.points[i].copy(point).addScaledVector(normal, HALF_RAILS_GAUGE)
      rightCatmull.points[i].copy(point).addScaledVector(normal, -HALF_RAILS_GAUGE)
    }

    if (leftGeoRef.current) {
      const geo = new TubeGeometry(leftCatmull, LEFT_TUBE_SEGMENTS, RAIL_RADIUS, RADIAL_SEGMENTS)
      leftGeoRef.current.copy(geo)
      geo.dispose()
    }
    if (rightGeoRef.current) {
      const geo = new TubeGeometry(rightCatmull, RIGHT_TUBE_SEGMENTS, RAIL_RADIUS, RADIAL_SEGMENTS)
      rightGeoRef.current.copy(geo)
      geo.dispose()
    }

    splinesGroupsRef.current.forEach((group, i) => {
      if (!group) return
      const u = i / (SPLINES_COUNT - 1)
      liveCurve.getPointAt(u, point)
      liveCurve.getTangentAt(u, tangent)
      group.position.set(point.x, 0, point.z)
      group.rotation.y = Math.atan2(tangent.x, tangent.z)
    })
  })

  const initialCurve = useMemo(
    () => new QuadraticBezierCurve3(pivot, CENTER, targetOffset),
    [pivot, targetOffset],
  )
  const initialRailCurves = useMemo(() => buildRailsCurves(initialCurve), [initialCurve])
  const initialSplines = useMemo(() => sampleSplines(initialCurve, SPLINES_COUNT), [initialCurve])

  return (
    <group>
      {phase === GamePhase.PLAYING && !occupied && (
        <SphereHitbox
          radius={0.8}
          position-y={0.5}
          onPointerOver={() => setHighlight(true)}
          onPointerOut={() => setHighlight(false)}
          onClick={e => {
            e.stopPropagation()
            handleToggleSwitch(x, y)
            sounds?.switch.play()
          }}
        />
      )}

      {/* Rails */}
      <Select enabled={highlight}>
        <group position-y={RAIL_HEIGHT} scale-y={RAIL_SCALE_Y}>
          <mesh receiveShadow material={METAL_MATERIAL}>
            <tubeGeometry ref={leftGeoRef} args={[initialRailCurves.leftCurve, 8, RAIL_RADIUS]} />
          </mesh>
          <mesh receiveShadow material={METAL_MATERIAL}>
            <tubeGeometry
              ref={rightGeoRef}
              args={[initialRailCurves.rightCurve, 20, RAIL_RADIUS]}
            />
          </mesh>
        </group>
      </Select>

      {/* Splines */}
      <group position-y={SPLINE_HEIGHT * 0.5}>
        {initialSplines.map((spline, i) => {
          if (i === 0 || i === initialSplines.length - 1) return null
          return (
            <group
              key={i}
              ref={el => (splinesGroupsRef.current[i] = el)}
              position={[spline.position.x, 0, spline.position.z]}
              rotation-y={spline.rotationY}
            >
              <SplineModel isSwitch highlight={highlight} />
            </group>
          )
        })}
      </group>

      <SwitchIndicatorModel
        position={CORNER_OFFSETS[common].clone().multiplyScalar(0.45)}
        targetRotation={ARROW_ROTATIONS[target]}
        occupied={occupied}
        highlight={highlight}
      />
    </group>
  )
}
